import { ActionConfirmation, useHasActionOutcomeHost, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useState } from "react";
import { get, type Booking, type BookingList } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { formatDate, formatTime, isoDay } from "../format.js";
import { useAsync } from "../useAsync.js";
import { diaryPaymentCanResolveOnSite, diaryPaymentClassifications, type DiaryPaymentClassification } from "./diaryPayments.js";
import { MarkPaymentReceived } from "./PaymentOperations.js";

/** The Hub caps one Diary read at this many booking rows (bookingReads.ts). */
export const DIARY_READ_LIMIT = 1000;

/** The UTC day the Hub's Diary window uses for a lesson start. */
function diaryDay(startsAt: string): string {
  return new Date(startsAt).toISOString().slice(0, 10);
}

export function eligiblePaymentLessons(bookings: Booking[], studentId: string, payments: DiaryPaymentClassification[]) {
  const active = bookings.filter((row) => !row.isSchools && !["canceled", "rejected", "waiting"].includes(row.status)
    && !["canceled", "rejected", "waiting"].includes(row.bookingStatus));
  const counts = new Map<number, Set<string>>();
  for (const row of active) {
    if (row.externalId === null) continue;
    const participants = counts.get(row.externalId) ?? new Set<string>();
    participants.add(row.bookingId);
    counts.set(row.externalId, participants);
  }
  return active.flatMap((lesson) => {
    const payment = payments.find((item) => item.appointment_id === lesson.externalId);
    if (lesson.studentId !== studentId || lesson.externalId === null || counts.get(lesson.externalId)?.size !== 1
      || !payment || !diaryPaymentCanResolveOnSite(payment) || payment.payment_id === null || payment.amount_due === null) return [];
    return [{ lesson, payment }];
  }).filter((row, index, rows) => rows.findIndex((other) => other.lesson.externalId === row.lesson.externalId) === index);
}

export function LessonPaymentPicker({ studentId, onCancel }: { studentId: string; onCancel: () => void }) {
  const [editing, setEditing] = useState(false);
  const [from, setFrom] = useState(() => isoDay(-30));
  const [to, setTo] = useState(() => isoDay(30));
  return <section>
    <h2>Choose an unpaid lesson</h2>
    <div className="fields">
      <label>From<input type="date" disabled={editing} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
      <label>To<input type="date" disabled={editing} value={to} onChange={(event) => setTo(event.target.value)} /></label>
    </div>
    {from && to && from <= to ? <LessonPaymentList key={`${studentId}:${from}:${to}`} studentId={studentId} from={from} to={to} onEditing={setEditing} />
      : <p role="alert">Choose a valid date range.</p>}
    {!editing && <p><button className="quiet" onClick={onCancel}>Pay for something else</button></p>}
  </section>;
}

function LessonPaymentList({ studentId, from, to, onEditing }: { studentId: string; from: string; to: string; onEditing: (editing: boolean) => void }) {
  const hosted = useHasActionOutcomeHost();
  const { me } = useAuth();
  const [selected, setSelected] = useState<ReturnType<typeof eligiblePaymentLessons>[number] | null>(null);
  useEffect(() => { onEditing(selected !== null); }, [selected, onEditing]);
  const [complete, setComplete] = useState<ActionOutcome | null>(null);
  // The re-read awaited after a payment, so the form only closes on fresh data
  // and the list is not read a second time to show it.
  const [refreshed, setRefreshed] = useState<Awaited<ReturnType<typeof load>> | null>(null);
  async function load() {
    const scope = isOwner(me) ? {} : { scope: "mine" };
    // An owner's whole-school window easily passes the read cap, so the
    // student filter only nominates candidates: it hides anybody else in the
    // same lesson and cannot prove an appointment is individual.
    const own = await get<BookingList>("/admin/bookings", { from, to, studentId, ...scope });
    if (own.bookings.length >= DIARY_READ_LIMIT) return { rows: [], truncated: true };
    const candidates = own.bookings.filter((row) => row.studentId === studentId && !row.isSchools && row.externalId !== null);
    const classified = await diaryPaymentClassifications([...new Set(candidates.map((row) => row.externalId as number))]);
    const payments = classified.filter(diaryPaymentCanResolveOnSite);
    const pending = new Set(payments.map((payment) => payment.appointment_id));
    const days = [...new Set(candidates.filter((row) => pending.has(row.externalId as number)).map((row) => diaryDay(row.startsAt)))];
    // Participants are counted from each candidate day's full scoped Diary, so
    // group participants cannot disappear. Anonymous colleague rows never
    // become actions.
    const diaries = await Promise.all(days.map((day) => get<BookingList>("/admin/bookings", { from: day, to: day, ...scope })));
    if (diaries.some((diary) => diary.bookings.length >= DIARY_READ_LIMIT)) {
      throw new Error("Too many lessons on one of these days to check who else attends. Log this payment from the Diary instead.");
    }
    return { rows: eligiblePaymentLessons(diaries.flatMap((diary) => diary.bookings), studentId, payments), truncated: false };
  }
  const data = useAsync(load, [studentId, from, to]);
  if (selected) return <MarkPaymentReceived key={selected.lesson.bookingId} lesson={{
    portal_student_id: studentId, student_name: selected.lesson.studentName ?? "Student",
    tutor_name: selected.lesson.tutorName ?? "Tutor", service_name: selected.lesson.serviceName ?? "Lesson",
    starts_at: selected.lesson.startsAt, amount_due: selected.payment.amount_due!,
    appointment_id: selected.payment.appointment_id, payment_id: selected.payment.payment_id!,
    payment_state: selected.payment.status,
  }} onCancel={() => setSelected(null)} onCompleted={async (outcome) => {
    setRefreshed(await load());
    setComplete(outcome);
    setSelected(null);
  }} />;
  const list = refreshed ?? data.data;
  if (!refreshed && data.loading) return <p role="status">Checking unpaid lessons…</p>;
  if (!refreshed && data.error) return <p role="alert">{data.error}</p>;
  // A capped read of this student's lessons may be missing some of them.
  if (list?.truncated) return <p role="alert">Too many lessons in this date range. Choose a shorter range to log a payment.</p>;
  return <>
    {complete && !hosted && <ActionConfirmation {...complete} reveal />}
    {list?.rows.length === 0 && <p>No eligible unpaid lessons in this date range. Only pending on-site payments for individual lessons can be logged here.</p>}
    <div className="stack">{list?.rows.map(({ lesson, payment }) => <button className="quiet" key={lesson.bookingId}
      onClick={() => setSelected({ lesson, payment })}>
      {formatDate(lesson.startsAt)} · {formatTime(lesson.startsAt)} · {lesson.serviceName} with {lesson.tutorName} · £{payment.amount_due}
    </button>)}</div>
  </>;
}
